import { useEffect, useRef, useState } from 'react';
import { Zap } from 'lucide-react';
import { formatNumber } from '../../lib/format-number';

interface CreditsBannerProps {
  balance: number;
  compact?: boolean;
  pulse?: boolean;
  onClick?: () => void;
}

export function CreditsBanner({ balance, compact = false, pulse = false, onClick }: CreditsBannerProps) {
  const [displayed, setDisplayed] = useState(balance);
  const prevRef = useRef(balance);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = balance;
    if (prev === balance) return;

    const diff = balance - prev;
    const steps = Math.min(Math.abs(diff), 20);
    const stepSize = diff / steps;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      if (step >= steps) {
        setDisplayed(balance);
        clearInterval(interval);
      } else {
        setDisplayed(Math.round(prev + stepSize * step));
      }
    }, 30);

    return () => clearInterval(interval);
  }, [balance]);

  const formatted = formatNumber(displayed);

  if (compact) {
    return (
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200${pulse ? ' animate-pulse ring-2 ring-amber-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900' : ''}`}
      >
        <Zap size={12} className="fill-current" />
        {formatted}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 dark:from-amber-500/20 dark:to-orange-500/20 border border-amber-200 dark:border-amber-700/50 hover:from-amber-500/15 hover:to-orange-500/15 transition-all duration-200"
    >
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
        <Zap size={16} className="text-white fill-current" />
      </div>
      <div className="text-left">
        <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{formatted}</p>
        <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">Check-in Credits</p>
      </div>
    </button>
  );
}
